import React from "react";
import { Formik } from "formik";
import { Form } from "./form";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  BmiAnswer,
  HeightUnits,
  IncomingScoringData,
  PrepFqProfile,
  WeightUnits,
} from "../../../../../models/users";
import { rSetAlert } from "../../../../../reducers/session/actions";
import { useAuth } from "../../../../../firebase/useAuth";
import usePrepFqProfile from "../../../fetchers/usePrepFqProfile";
import usePrepFqAnswers from "../../../fetchers/usePrepFqAnswers";
import useUser from "../../../../../firebase/useUser";
import { PrepFqQuestion } from "../../../../../models/prepFqQuiz";
import { calculateAge } from "../../../../../utils/date";
import logger from "../../../../../utils/logger";
import { PwgError } from "../../../../../models/common";
import { useRouter } from "next/router";
import { doc, getFirestore, writeBatch } from "firebase/firestore";
import { firebaseApp } from "../../../../../firebase/firebaseClient";
import { getFunctions, httpsCallable } from "firebase/functions";

interface Props {
  questions: PrepFqQuestion[];
}

const validationSchema = Yup.object({
  weight_units: Yup.string().required("bmi.weightUnits.error"),
  height_units: Yup.string().required("bmi.heightUnits.error"),
  weight: Yup.number().when(
    ["prefer_not_to_answer"],
    ([preferNotToAnswer], schema) => {
      return preferNotToAnswer === false
        ? schema.min(1, "bmi.weight.error").required("bmi.weight.error")
        : schema;
    }
  ),
  height_cm: Yup.number().when(
    ["prefer_not_to_answer"],
    ([preferNotToAnswer], schema) => {
      return preferNotToAnswer === false
        ? Yup.number().when(["height_units"], ([heightUnits], innerSchema) => {
            return heightUnits === "cm"
              ? innerSchema
                  .min(1, "bmi.height.error")
                  .required("bmi.height.error")
              : innerSchema;
          })
        : schema;
    }
  ),
  height_in: Yup.number().when(
    ["prefer_not_to_answer"],
    ([preferNotToAnswer], schema) => {
      return preferNotToAnswer === false
        ? Yup.number().when(["height_units"], ([heightUnits], innerSchema) => {
            return heightUnits === "inches"
              ? innerSchema.required("bmi.height.error")
              : innerSchema;
          })
        : schema;
    }
  ),
  height_ft: Yup.number().when(
    ["prefer_not_to_answer"],
    ([preferNotToAnswer], schema) => {
      return preferNotToAnswer === "inches"
        ? Yup.number().when(["height_units"], ([heightUnits], innerSchema) => {
            return heightUnits === "inches"
              ? innerSchema.required("bmi.height.error")
              : innerSchema;
          })
        : schema;
    }
  ),
  prefer_not_to_answer: Yup.boolean(),
});

const getValidValue = (value: number) => {
  if (value) {
    return value.toString();
  } else {
    return "";
  }
};

const BmiFormLite: React.FC<Props> = ({ questions }) => {
  const { t } = useTranslation(["prepFq", "common"]);
  const dispatch = useDispatch();
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const router = useRouter();
  const { profile, mutate: mutateProfile } = usePrepFqProfile(user?.user_id);
  const { answers, mutate: mutateAnswers } = usePrepFqAnswers(
    profile?.prep_fq_profile_id,
    questions
  );
  const userBmiAnswer: BmiAnswer | null =
    answers?.length > 0 ? (answers[0] as any) : null;

  const setUpValues = (answer: BmiAnswer) => {
    let userValues = {
      weight: "",
      height_cm: "",
      height_ft: "",
      height_in: "",
      weight_units: "lbs",
      height_units: "inches",
      prefer_not_to_answer: false,
    };
    if (answer) {
      userValues.weight = getValidValue(answer.weight);
      userValues.height_cm =
        answer.height_units === "cm" ? getValidValue(answer.height) : "";
      userValues.height_ft =
        answer.height_units === "inches" && answer.height
          ? ((answer.height - (answer.height % 12)) / 12).toString()
          : "";
      userValues.height_in =
        answer.height_units === "inches" && answer.height
          ? (answer.height % 12).toString()
          : "";
      userValues.weight_units = answer.weight_units;
      userValues.height_units = answer.height_units;
      userValues.prefer_not_to_answer = answer.prefer_not_to_answer;
    }
    return userValues;
  };
  const values = setUpValues(userBmiAnswer);

  const onSubmit = async (submission: typeof values) => {
    try {
      let weight = 0;
      let height = 5 * 12;
      let bmi = 0;
      if (submission.prefer_not_to_answer) {
        bmi = 99;
      } else {
        weight = parseFloat(submission.weight);
        height =
          submission.height_units === "inches"
            ? parseFloat(submission.height_ft) * 12 +
              parseFloat(submission.height_in)
            : parseFloat(submission.height_cm);

        const heightInMeters =
          submission.height_units === "cm" ? height / 100 : height / 39.37;
        const weightInKilos =
          submission.weight_units === "kgs" ? weight : weight / 2.205;

        bmi = weightInKilos / (heightInMeters * heightInMeters);
      }

      const bmiAnswer: BmiAnswer = {
        weight,
        weight_units: submission.weight_units as WeightUnits,
        height_units: submission.height_units as HeightUnits,
        height,
        bmi,
        answer_id: "bmi",
        question_id: "bmi",
        prefer_not_to_answer: submission.prefer_not_to_answer,
        question_type: "bmi",
      };
      const profileUpdate: PrepFqProfile = {
        date_last_updated: new Date().getTime(),
      } as PrepFqProfile;
      mutateAnswers([bmiAnswer], false);
      const firestore = getFirestore(firebaseApp);
      const batch = writeBatch(firestore);
      batch.set(
        doc(
          firestore,
          "prep_fq_profiles",
          profile?.prep_fq_profile_id,
          "answers",
          "bmi"
        ),
        bmiAnswer
      );
      batch.update(
        doc(firestore, "prep_fq_profiles", profile?.prep_fq_profile_id),
        profileUpdate as any
      );
      await batch.commit();
      mutateAnswers();
      const functions = getFunctions(firebaseApp);
      const onCalculatePrepFqScore = httpsCallable(
        functions,
        "calculatePrepFqScore"
      );
      await onCalculatePrepFqScore({
        prep_fq_profile_id: profile?.prep_fq_profile_id,
        gender: user.gender,
        user_id: user.user_id,
        private_key: "",
        age: calculateAge(user?.date_of_birth),
      } as IncomingScoringData);
      mutateProfile();
    } catch (error) {
      const pwgError: PwgError = {
        user_id: authUser?.user?.uid,
        ssr: false,
        context: "onSubmit: BmiFormLite",
        date_created: new Date().getTime(),
        error: error?.toString(),
        pathname: router.asPath,
      };
      await logger.e(
        "🚀 ~ file: BmiFormLite.tsx ~ line 294 ~ onSubmit ~ error",
        error,
        pwgError
      );
      dispatch(
        rSetAlert({
          key: new Date().getTime().toString(),
          message: t("prepFq:error"),
          type: "error",
        })
      );
    }
  };
  return (
    <React.Fragment>
      <Formik
        onSubmit={onSubmit}
        initialValues={values}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {(props) => <Form {...props} />}
      </Formik>
    </React.Fragment>
  );
};

export default BmiFormLite;
